import React, { Suspense, lazy } from 'react';
import { LinearProgress } from '@mui/material';
import ErrorMsg from '../../common/ErrorMsg';
import errorResponses from '../../../errorResponses';
import useGlynt from '../../hooks/useGlynt';
import { AUTH_CONFIG } from '../../../auth0-config';
import { AppContext } from '../../../App';
import { IDocument, IField, IGroundTruth, IToken } from '../../../types/my-types';
import { MarkDispatchContext, MarkStateContext } from '../../../context/MarkContext';
import { useGroundTruthCreate } from '../../hooks/useGroundTruthCreate';
import { useGroundTruthDelete } from '../../hooks/useGroundTruthDelete';
import { useGroundTruthUpdate } from '../../hooks/useGroundTruthUpdate';
import { usePage } from '../../hooks/usePage';
import { useParams } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { getNextActiveField } from '../../../utils/getNextActiveField';

const Page = lazy(() => import('../../tool/doc/Page'));

export interface MarkPageProps {
  pageUrl: string;
  page: {
    pageWidth: number;
    pageHeight: number;
    pageBoundingWidth: number;
    pageBoundingHeight: number;
    pageTokens: Array<IToken>;
    pagePhrases: Array<IToken>;
    pageSubPhrases: Array<Array<IToken>>;
  };
  markedTokens: IToken[];
  doc: IDocument;
  fields: IField[];
  groundTruths: IGroundTruth[];
}

const MarkPage: React.FC<MarkPageProps> = ({
  pageUrl,
  page,
  markedTokens,
  doc,
  fields,
  groundTruths,
}) => {
  const { setToast } = React.useContext(AppContext);
  const { activeField, tokens } = React.useContext(MarkStateContext);
  const dispatch = React.useContext<React.Dispatch<any>>(MarkDispatchContext);

  const { dpid: dataPooLid, id: trainingSetId } = useParams<{ dpid: string; id: string }>();

  if (!dataPooLid || !trainingSetId) {
    throw new Error('dataPooLid or trainingSetId is not defined');
  }

  const glynt = useGlynt();
  const cache = useQueryClient();

  const mutationGroundTruthCreate = useGroundTruthCreate(true, {
    onSuccess: () => {
      setToast({
        variant: 'success',
        open: true,
        message: 'Ground Truth Saved',
      });
      cache.invalidateQueries({
        queryKey: ['groundTruths']
      });
      cache.invalidateQueries({
        queryKey: ['fields']
      });
    },
    onError: () => {
      cache.invalidateQueries({
        queryKey: ['groundTruths']
      });
      setToast({
        variant: 'error',
        open: true,
        message: errorResponses.failedToSaveGroundTruth,
      });
    },
  });

  const mutationGroundTruthUpdate = useGroundTruthUpdate(true, {
    onSuccess: () => {
      setToast({
        variant: 'success',
        open: true,
        message: 'Ground Truth Saved',
      });
      cache.invalidateQueries({
        queryKey: ['groundTruths']
      });
      cache.invalidateQueries({
        queryKey: ['fields']
      });
    },
    onError: () => {
      cache.invalidateQueries({
        queryKey: ['groundTruths']
      });
      setToast({
        variant: 'error',
        open: true,
        message: errorResponses.failedToSaveGroundTruth,
      });
    },
  });

  const mutationGroundTruthDelete = useGroundTruthDelete(true, {
    onSuccess: () => {
      setToast({
        variant: 'success',
        open: true,
        message: 'Ground Truth Deleted.',
      });
      cache.invalidateQueries({
        queryKey: ['groundTruths']
      });
    },
    onError: () => {
      setToast({
        variant: 'error',
        open: true,
        message: errorResponses.failedToDeleteGroundTruth,
      });
      cache.invalidateQueries({
        queryKey: ['groundTruths']
      });
    },
  });

  const url = pageUrl.substring(AUTH_CONFIG.apiUrl!.length);
  const { data, isLoading, isError } = usePage(url);

  if (isLoading) {
    return <LinearProgress />;
  }

  if (isError) {
    return <ErrorMsg error="Sorry, failed to load document pages." />;
  }

  const imageUrl = data?.file_temp_url || '';
  const field = fields.find((field) => field.label === activeField);

  const saveGroundTruth = async () => {
    if (tokens.length !== 0) {
      try {
        const token_data = { tokens };
        const content = tokens.map((t: IToken) => t.content).join(' ');
        const groundTruth = groundTruths.find((g) => g.field === field!.url);
        if (groundTruth) {
          await mutationGroundTruthUpdate.mutate({
            trainingSetId,
            id: groundTruth.id,
            mutationData: {
              document: doc.id,
              content,
              token_data,
            },
          });
        } else {
          await mutationGroundTruthCreate.mutate({
            trainingSetId,
            mutationData: {
              field: field!.id,
              document: doc.id,
              content,
              token_data,
            },
          });
        }
      } catch (error) {
        alert("Sorry, Can't save ground truth");
      }
    }
  };

  const removeGroundTruth = async () => {
    try {
      const groundTruth = groundTruths.find((g) => g.field === field!.url);
      if (groundTruth) {
        await mutationGroundTruthDelete.mutate({
          trainingSetId,
          document: doc.id,
          mutationData: { id: groundTruth.id },
        });
      }
    } catch (error) {
      alert("Sorry, Can't delete ground truth");
    }
  };

  const handleKeyDown = async (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.which === 13) {
      dispatch({
        type: 'IS_SAVING',
        payload: { isSaving: true },
      });
      await saveGroundTruth();

      const nextField = getNextActiveField(fields as any, field!.label);

      dispatch({
        type: 'SAVE_FIELD',
        payload: {
          fieldsCount: fields.length,
          ...(nextField && { nextActiveField: nextField }),
        },
      });

      dispatch({
        type: 'IS_SAVING',
        payload: { isSaving: false },
      });
    } else if (e.key === 'Backspace' || e.which === 8) {
      await removeGroundTruth();
      dispatch({ type: 'RESET_TOKENS' });
    }
  };

  return (
    <Suspense fallback={<LinearProgress />}>
      <Page
        imageUrl={imageUrl}
        page={page}
        markedTokens={markedTokens}
        handleKeyDown={handleKeyDown}
      />
    </Suspense>
  );
};

export default MarkPage;
